// File: components/mobile/AndroidLogin.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase'; // Adjusted path for your structure
import { Loader2, Lock, Smartphone, AlertCircle } from 'lucide-react';

const AndroidLogin = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 1. Construct the Pseudo-Email
      const email = `${mobile}@packer.app`; 

      // 2. Authenticate
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email,
        password: pin,
      });

      if (authError) throw new Error("Invalid Mobile or PIN");
      if (!authData.user) throw new Error("No user found");

      // 3. Check Role
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', authData.user.id)
        .single();

      if (profile?.role !== 'packer') {
         await supabase.auth.signOut();
         throw new Error("Access Denied: Packers Only");
      }

      // 4. Redirect to Scanner
      navigate('/android-packer');

    } catch (err: any) {
      console.error("Login Failed:", err);
      setError(err.message || "Login failed");
      if (navigator.vibrate) navigator.vibrate(200); 
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col items-center justify-center p-6 text-white">
      <div className="mb-8 text-center">
        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/20">
            <Lock className="text-yellow-400 w-8 h-8" />
        </div>
        <h1 className="text-2xl font-black tracking-tight">PACKER ACCESS</h1>
        <p className="text-white/50 text-sm mt-1">Enter credentials to start</p>
      </div>

      <form onSubmit={handleLogin} className="w-full max-w-xs flex flex-col gap-4">
        <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50">
                <Smartphone size={20} />
            </div>
            <input 
                type="tel" 
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} 
                placeholder="Mobile Number"
                className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-lg font-bold tracking-widest focus:outline-none focus:border-yellow-400/50 text-white"
                required
            />
        </div>

        <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50">
                <Lock size={20} />
            </div>
            <input 
                type="password" 
                inputMode="numeric" 
                value={pin}
                onChange={(e) => setPin(e.target.value.slice(0, 6))}
                placeholder="6-Digit PIN"
                className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-lg font-bold tracking-widest focus:outline-none focus:border-yellow-400/50 text-white"
                required
            />
        </div>

        {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-200 text-sm">
                <AlertCircle size={16} />
                <span>{error}</span>
            </div>
        )}

        <button 
            type="submit" 
            disabled={loading || mobile.length < 10 || pin.length < 4}
            className="mt-4 bg-yellow-400 text-black font-black text-lg py-4 rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
        >
            {loading ? <Loader2 className="animate-spin" /> : "START PACKING"}
        </button>
      </form>
    </div>
  );
};

export default AndroidLogin;
